/**
 * V287: Google Play Billing Plugin Interface
 * 
 * TypeScript interface for native Android Google Play Billing plugin.
 * This replaces react-native-iap which doesn't work in Capacitor builds.
 */

import { registerPlugin } from '@capacitor/core';

/**
 * Product details from Google Play
 */
export interface GooglePlayProduct {
  productId: string;
  title: string;
  description: string;
  productType: string;
  price: string;
  priceAmountMicros: number;
  priceCurrencyCode: string;
  billingPeriod: string;
  offerToken: string; // CRITICAL: Required for purchase flow
}

/**
 * Purchase details from Google Play
 */
export interface GooglePlayPurchase {
  orderId: string;
  packageName: string;
  purchaseToken: string;
  purchaseTime: number;
  purchaseState: number;
  acknowledged: boolean;
  productId: string;
  products: string[];
}

/**
 * Purchase update event
 */
export interface PurchaseUpdateEvent {
  type: 'purchase_updated' | 'purchase_canceled' | 'purchase_error';
  purchase?: GooglePlayPurchase;
  error?: string;
  code?: number;
}

/**
 * V287.4: Purchase result from requestSubscription
 */
export interface PurchaseResult {
  success: boolean;
  purchaseToken?: string;
  isRestore?: boolean;
  message?: string;
  errorCode?: string;
  errorMessage?: string;
}

/**
 * Google Play Billing Plugin
 */
export interface GooglePlayBillingPlugin {
  /**
   * Initialize connection to Google Play Billing
   * 
   * @returns Promise resolving to success status
   */
  initConnection(): Promise<{ success: boolean }>;
  
  /**
   * Query subscription products from Google Play
   * 
   * @param options - Object containing array of product IDs (skus)
   * @returns Promise resolving to array of products
   */
  getSubscriptions(options: { skus: string[] }): Promise<{ products: GooglePlayProduct[] }>;
  
  /**
   * V300: Launch billing flow for subscription purchase with Identity-Aware Billing
   *
   * @param options - Object containing productId, offerToken, and identity binding fields
   * @returns Promise resolving to purchase result with token or error
   */
  requestSubscription(options: {
    productId: string;
    offerToken: string;
    obfuscatedAccountId?: string;  // V300: BlissNet user UUID (non-PII)
    obfuscatedProfileId?: string;  // V300: Profile UUID (non-PII)
  }): Promise<PurchaseResult>;
  
  /**
   * Get available purchases (for restore)
   * 
   * @returns Promise resolving to array of purchases
   */
  getAvailablePurchases(): Promise<{ purchases: GooglePlayPurchase[] }>;
  
  /**
   * Acknowledge a purchase (after server verification)
   * 
   * @param options - Object containing purchaseToken
   * @returns Promise resolving to success status
   */
  acknowledgePurchase(options: { purchaseToken: string }): Promise<{ success: boolean }>;
  
  /**
   * End billing connection
   * 
   * @returns Promise resolving to success status
   */
  endConnection(): Promise<{ success: boolean }>;
  
  /**
   * Add listener for purchase updates
   * 
   * @param eventName - Event name ('purchaseUpdate')
   * @param listenerFunc - Callback function
   */
  addListener(
    eventName: 'purchaseUpdate',
    listenerFunc: (event: PurchaseUpdateEvent) => void
  ): Promise<any>;
  
  /**
   * Remove all listeners
   */
  removeAllListeners(): Promise<void>;
}

const GooglePlayBilling = registerPlugin<GooglePlayBillingPlugin>('GooglePlayBilling', {
  web: () => ({
    // Web stub - billing only available on Android
    initConnection: async () => {
      throw new Error('Google Play Billing is only available on Android');
    },
    getSubscriptions: async () => {
      throw new Error('Google Play Billing is only available on Android');
    },
    requestSubscription: async () => {
      throw new Error('Google Play Billing is only available on Android');
    },
    getAvailablePurchases: async () => {
      throw new Error('Google Play Billing is only available on Android');
    },
    acknowledgePurchase: async () => {
      throw new Error('Google Play Billing is only available on Android');
    },
    endConnection: async () => {
      throw new Error('Google Play Billing is only available on Android');
    },
    addListener: async () => {
      return { remove: () => {} };
    },
    removeAllListeners: async () => {},
  }),
});

export default GooglePlayBilling;

